import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertCircle, ArrowDown, Ban, Check, CheckCheck, ChevronLeft, ChevronRight, Clock, Download, Eye, EyeOff, ExternalLink, Loader2, LogOut, MessagesSquare, Pause, Play, Reply, RotateCw, Trash2, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { accountOfflineReason, bytesHuman, cn, fmtDuration, fmtTime, initials, colorFromString, mediaKindLabel, messagePreview } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import { useWorkspace } from "@/store/workspace";
import { useEvents } from "@/store/events";
import { useOutbox, type PendingMessage } from "@/store/outbox";
import { useMediaDownloads } from "@/store/mediaDownloads";
import { Composer } from "@/components/Composer";
import type { Account, AccountUnread, Chat, Message, MessageButton, MessageButtons } from "@/lib/types";

const PAGE_SIZE = 100;
// Через сколько без ответа воркера честно говорим, что загрузка затянулась.
// Это не провал: команда могла встать в очередь за другой фоновой работой
// аккаунта. Провал — только явный command.failed/rejected.
const HISTORY_SLOW_MS = 30_000;

export function MessagePane({
  showAccountBadge = false,
  accounts = [],
}: {
  showAccountBadge?: boolean;
  accounts?: Account[];
} = {}) {
  const qc = useQueryClient();
  const { push } = useToast();
  const { selectedAccountId, selectedChatId, selectChat } = useWorkspace();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  // Прилип ли вьюпорт к низу. Пока прилип — новые сообщения подматываются
  // сами; если оператор ушёл вверх читать историю, дёргать его нельзя.
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [missedCount, setMissedCount] = useState(0);

  const outboxItems = useOutbox((s) => s.items);

  const { data: chats = [] } = useQuery({
    queryKey: ["chats", selectedAccountId],
    queryFn: () => api.listChats(selectedAccountId!),
    enabled: !!selectedAccountId,
  });
  const chat = useMemo(() => chats.find((c: Chat) => c.id === selectedChatId), [chats, selectedChatId]);
  // Аккаунт не на связи — всё, что видно, это архив из БД, а не живая переписка,
  // и дёргать воркера за историей бесполезно.
  const offline = accountOfflineReason(accounts.find((a) => a.id === selectedAccountId) ?? null);

  const {
    data,
    isFetching,
    isSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["messages", selectedAccountId, selectedChatId, showDeleted],
    queryFn: ({ pageParam }) =>
      api.listMessages(selectedAccountId!, selectedChatId!, {
        limit: PAGE_SIZE,
        include_deleted: showDeleted,
        before_id: pageParam,
      }),
    enabled: !!(selectedAccountId && selectedChatId),
    initialPageParam: undefined as number | undefined,
    // Каждая страница приходит newest-first; следующая «старее» — берём
    // минимальный tg_message_id как курсор (before_id эксклюзивен). Неполная
    // страница означает, что история закончилась.
    getNextPageParam: (lastPage) =>
      lastPage.length < PAGE_SIZE
        ? undefined
        : Math.min(...lastPage.map((m) => m.tg_message_id)),
  });
  const messages = useMemo(() => (data ? data.pages.flat() : []), [data]);
  // Отправленное оператором, но ещё не подтверждённое Telegram.
  const pendingList = useMemo(
    () => outboxItems.filter((i) => i.chatId === selectedChatId),
    [outboxItems, selectedChatId]
  );

  // Виртуализатору нужен плоский список: разделители дней, сообщения и
  // пузыри очереди отправки идут одной последовательностью с устойчивыми
  // ключами — по ним же держится позиция скролла при догрузке истории.
  const rows = useMemo(() => buildRows(messages, pendingList), [messages, pendingList]);
  const indexByTgId = useMemo(() => {
    const map = new Map<number, number>();
    rows.forEach((r, i) => {
      if (r.kind === "message") map.set(r.message.tg_message_id, i);
      else if (r.kind === "album") for (const m of r.messages) map.set(m.tg_message_id, i);
    });
    return map;
  }, [rows]);

  // Историю чата воркер тянет из Telegram по запросу: до первой загрузки в
  // базе лежит максимум последнее сообщение из синка диалогов, так что пусто
  // здесь ≠ пусто в Telegram. Признак «уже тянули» — chat.history_synced_at;
  // считать по messages.length нельзя, превью-сообщение сбивало бы счёт.
  const [historyState, setHistoryState] = useState<"idle" | "loading" | "slow" | "failed">("idle");
  const historyCmdRef = useRef<string | null>(null);
  const historyTimerRef = useRef<number | null>(null);
  const autoBackfilledRef = useRef<Set<string>>(new Set());

  const loadHistory = useCallback(() => {
    if (!selectedAccountId || !selectedChatId) return;
    const key = `${selectedAccountId}:${selectedChatId}`;
    autoBackfilledRef.current.add(key);
    historyCmdRef.current = null;
    setHistoryState("loading");
    if (historyTimerRef.current) window.clearTimeout(historyTimerRef.current);
    historyTimerRef.current = window.setTimeout(() => {
      setHistoryState((cur) => (cur === "loading" ? "slow" : cur));
    }, HISTORY_SLOW_MS);
    api.backfillChat(selectedAccountId, selectedChatId, PAGE_SIZE).then(
      (r) => {
        historyCmdRef.current = r.command_id;
      },
      () => {
        autoBackfilledRef.current.delete(key);
        setHistoryState("failed");
      }
    );
  }, [selectedAccountId, selectedChatId]);

  // Смена чата — сбрасываем состояние загрузки истории предыдущего.
  useEffect(() => {
    setHistoryState("idle");
    historyCmdRef.current = null;
    if (historyTimerRef.current) window.clearTimeout(historyTimerRef.current);
  }, [selectedChatId]);

  // Открыли чат, историю которого ещё ни разу не тянули, — тянем.
  useEffect(() => {
    if (!selectedAccountId || !selectedChatId || !isSuccess) return;
    if (!chat || chat.history_synced_at || offline) return;
    if (autoBackfilledRef.current.has(`${selectedAccountId}:${selectedChatId}`)) return;
    loadHistory();
  }, [selectedAccountId, selectedChatId, isSuccess, chat, offline, loadHistory]);

  // Сообщения и список чатов обновляет useLiveCache. Здесь — только то, что
  // завязано на локальное состояние панели: курсор догрузки и ответы ботов.
  useEffect(() => {
    if (!selectedAccountId || !selectedChatId) return;
    return useEvents.getState().subscribe(selectedAccountId, (e) => {
      if (e.type === "chat.backfilled") {
        const d = e.data as {
          chat_id?: string;
          fetched?: number;
          requested?: number;
          before_id?: number | null;
        };
        if (d.chat_id === selectedChatId) {
          // Первая загрузка истории (before_id пуст) доехала — снимаем «загрузку».
          if (d.before_id == null) {
            if (historyTimerRef.current) window.clearTimeout(historyTimerRef.current);
            setHistoryState("idle");
          }
          // Курсорная догрузка вглубь (before_id задан) завершилась: снимаем
          // флаг занятости, а если Telegram вернул меньше запрошенного —
          // помечаем, что достигли начала переписки и глубже не тянем.
          if (d.before_id != null) {
            const key = `${selectedAccountId}:${selectedChatId}`;
            deepFetchingRef.current.delete(key);
            if ((d.fetched ?? 0) < (d.requested ?? PAGE_SIZE)) {
              reachedTgStartRef.current.add(key);
            }
          }
        }
      }
      // Воркер не смог выполнить нашу загрузку истории — показываем это, а не
      // делаем вид, что в чате пусто.
      if (e.type === "command.failed" || e.type === "command.rejected") {
        const d = e.data as { command_id?: string };
        if (d.command_id && d.command_id === historyCmdRef.current) {
          if (historyTimerRef.current) window.clearTimeout(historyTimerRef.current);
          autoBackfilledRef.current.delete(`${selectedAccountId}:${selectedChatId}`);
          setHistoryState("failed");
        }
      }
      if (e.type === "bot.callback_answer") {
        const d = e.data as { chat_id?: string; text?: string | null; alert?: boolean };
        if (d.chat_id === selectedChatId && d.text) {
          push({ title: "Ответ бота", description: d.text, variant: d.alert ? "destructive" : "default" });
        }
      }
    });
  }, [selectedAccountId, selectedChatId, qc, push]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // Оценка для ещё не измеренных строк: короткий пузырь в одну строку.
    estimateSize: () => 56,
    getItemKey: (i) => rows[i]?.key ?? i,
    overscan: 8,
    // При догрузке истории сверху взгляд остаётся на той же строке: позиция
    // якорится по ключу и пересчитывается после реального замера подгруженного.
    // Подмотку вниз делаем сами (followOnAppend срабатывает раньше, чем чат
    // успевает наполниться, и первое открытие остаётся наверху).
    anchorTo: "end",
    // Воздух у краёв ленты. Через padding контейнера его дать нельзя —
    // виртуализатор считает смещения от начала своего сайзера.
    paddingStart: 12,
    paddingEnd: 12,
    // Подмотку вниз мы делаем из layout-эффекта, а синхронный flushSync
    // виртуализатора из фазы рендера React отвергает с ошибкой в консоли.
    useFlushSync: false,
  });
  // Чаты, для которых сейчас идёт курсорная догрузка из Telegram, и чаты,
  // догруженные до самого начала переписки (тянуть глубже нечего).
  const deepFetchingRef = useRef<Set<string>>(new Set());
  const reachedTgStartRef = useRef<Set<string>>(new Set());

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = bottomGap < 80;
    if (atBottom !== atBottomRef.current) {
      atBottomRef.current = atBottom;
      setShowJump(!atBottom);
      if (atBottom) setMissedCount(0);
    }
    if (el.scrollTop >= 120) return;
    // Пока в БД есть более старые страницы — листаем их (дёшево, без Telegram).
    if (hasNextPage) {
      if (isFetchingNextPage) return;
      fetchNextPage();
      return;
    }
    // БД исчерпана — дотягиваем более старое прямо из Telegram по курсору
    // (tg_message_id самого старого сообщения), пока не упрёмся в начало.
    if (!selectedAccountId || !selectedChatId || messages.length === 0) return;
    const key = `${selectedAccountId}:${selectedChatId}`;
    if (deepFetchingRef.current.has(key) || reachedTgStartRef.current.has(key)) return;
    deepFetchingRef.current.add(key);
    const oldest = Math.min(...messages.map((m) => m.tg_message_id));
    api.backfillChat(selectedAccountId, selectedChatId, PAGE_SIZE, oldest).catch(() => {
      deepFetchingRef.current.delete(key);
    });
  }

  const shownChatRef = useRef<string | null>(null);
  // Ключ последней строки, а не длина списка: список растёт и при догрузке
  // старой истории вверх, а это не «пропущенное сообщение».
  const lastKeyRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const lastKey = rows.length > 0 ? rows[rows.length - 1].key : null;

    // Смена чата — сразу к последнему сообщению.
    if (shownChatRef.current !== selectedChatId) {
      shownChatRef.current = selectedChatId;
      lastKeyRef.current = lastKey;
      atBottomRef.current = true;
      setShowJump(false);
      setMissedCount(0);
      virtualizer.scrollToIndex(Math.max(0, rows.length - 1), { align: "end" });
      return;
    }

    const appended = lastKey != null && lastKey !== lastKeyRef.current;
    lastKeyRef.current = lastKey;
    if (atBottomRef.current) {
      // Стоим внизу — держимся низа и на догруженных страницах, и на новых
      // сообщениях. Прыжок повторяется на каждое изменение списка, потому что
      // до реального замера строк «низ» ещё уезжает.
      virtualizer.scrollToIndex(Math.max(0, rows.length - 1), { align: "end" });
    } else if (appended) {
      // Оператор читает историю — не выдёргиваем его вниз, а показываем,
      // сколько сообщений пришло, пока он там.
      setMissedCount((n) => n + 1);
      setShowJump(true);
    }
  }, [rows, selectedChatId, virtualizer]);

  function jumpToBottom() {
    atBottomRef.current = true;
    setShowJump(false);
    setMissedCount(0);
    virtualizer.scrollToIndex(Math.max(0, rows.length - 1), { align: "end" });
  }

  /**
   * Открытый чат помечается прочитанным сам — оператору не нужно жать кнопку,
   * а бейдж не висит на диалоге, который он прямо сейчас читает.
   *
   * Только для активной вкладки: read receipt виден собеседнику, и отправлять
   * его из свёрнутого браузера — врать о том, что сообщение прочитали.
   */
  useEffect(() => {
    if (!selectedAccountId || !selectedChatId || !chat) return;
    if (chat.unread_count <= 0) return;
    if (document.visibilityState !== "visible") return;
    // Гасим бейджи сразу: команда воркеру асинхронная, а ждать её нечего.
    // Счётчик аккаунта правим тем же числом, иначе он вернётся на ближайшем
    // рефетче и мигнёт.
    const cleared = chat.unread_count;
    qc.setQueryData<Chat[]>(["chats", selectedAccountId], (old) =>
      old?.map((c) => (c.id === selectedChatId ? { ...c, unread_count: 0 } : c))
    );
    qc.setQueryData<AccountUnread[]>(["accounts", "unread"], (old) =>
      old
        ?.map((u) =>
          u.account_id === selectedAccountId
            ? {
                ...u,
                unread_chats: Math.max(0, u.unread_chats - 1),
                unread_messages: Math.max(0, u.unread_messages - cleared),
              }
            : u
        )
        .filter((u) => u.unread_messages > 0)
    );
    api.markRead(selectedAccountId, selectedChatId).catch(() => {
      /* воркер повторит на следующем открытии чата */
    });
  }, [selectedAccountId, selectedChatId, chat, qc]);

  const leaveChat = useMutation({
    mutationFn: () => api.leaveChat(selectedAccountId!, selectedChatId!),
    onSuccess: () => {
      push({ title: "Выходим из чата…" });
      qc.invalidateQueries({ queryKey: ["chats", selectedAccountId] });
      selectChat(null);
    },
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const blockUser = useMutation({
    mutationFn: () => api.blockUser(selectedAccountId!, selectedChatId!),
    onSuccess: () => push({ title: "Пользователь заблокирован" }),
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const deleteDialog = useMutation({
    mutationFn: () => api.deleteDialog(selectedAccountId!, selectedChatId!),
    onSuccess: () => {
      push({ title: "Удаляем диалог…" });
      qc.invalidateQueries({ queryKey: ["chats", selectedAccountId] });
      selectChat(null);
    },
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const clickButton = useMutation({
    mutationFn: ({ tgId, row, col, sharePhone }: { tgId: number; row: number; col: number; sharePhone?: boolean }) =>
      api.clickButton(selectedAccountId!, selectedChatId!, tgId, row, col, sharePhone),
    onError: (e) =>
      push({ title: "Не удалось нажать кнопку", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const sendText = useMutation({
    mutationFn: (text: string) => api.sendMessage(selectedAccountId!, selectedChatId!, { text }),
    onError: (e) =>
      push({ title: "Не удалось отправить", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  /** Повтор сбойной отправки: тот же текст уходит новой командой. */
  async function retryPending(item: PendingMessage) {
    if (!selectedAccountId || !selectedChatId) return;
    useOutbox.getState().remove(item.localId);
    const localId = `local:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    useOutbox.getState().add({
      localId,
      accountId: selectedAccountId,
      chatId: selectedChatId,
      text: item.text,
      fileName: item.fileName,
      mediaKind: item.mediaKind,
      replyToTgId: item.replyToTgId,
    });
    try {
      const res = await api.sendMessage(selectedAccountId, selectedChatId, {
        text: item.text,
        reply_to: item.replyToTgId,
      });
      useOutbox.getState().attachCommandId(localId, res.command_id);
    } catch (e) {
      useOutbox.getState().fail(localId, (e as ApiError).message ?? String(e));
    }
  }

  function handleButton(m: Message, row: number, col: number, btn: MessageButton) {
    if ((btn.kind === "url" || btn.kind === "webview") && btn.url) {
      window.open(btn.url, "_blank", "noopener,noreferrer");
      return;
    }
    if (btn.kind === "text") {
      // Reply-клавиатура: нажатие = отправка текста кнопки обычным сообщением.
      sendText.mutate(btn.text);
      return;
    }
    if (btn.kind === "callback") {
      clickButton.mutate({ tgId: m.tg_message_id, row, col });
      return;
    }
    if (btn.kind === "request_phone") {
      // Отправляет реальный номер аккаунта боту — подтверждаем явно.
      if (!window.confirm("Отправить боту номер телефона этого аккаунта?")) return;
      clickButton.mutate({ tgId: m.tg_message_id, row, col, sharePhone: true });
      return;
    }
    push({ title: "Кнопка не поддерживается", description: btn.kind, variant: "destructive" });
  }

  const buttonsBusy = clickButton.isPending || sendText.isPending;

  const byTgId = useMemo(() => {
    const map = new Map<number, Message>();
    for (const m of messages) map.set(m.tg_message_id, m);
    return map;
  }, [messages]);

  // Сбрасываем выбранный для ответа таргет и режим показа удалённых при смене чата.
  useEffect(() => {
    setReplyTo(null);
    setShowDeleted(false);
  }, [selectedChatId]);

  const account = accounts.find((a) => a.id === selectedAccountId) ?? null;
  const chatName = chat?.title || (chat?.username ? `@${chat.username}` : "Собеседник");
  const senderLabel = (m: Message) =>
    m.direction === "out"
      ? account
        ? account.first_name || (account.username ? `@${account.username}` : "Вы")
        : "Вы"
      : chatName;

  /** Переход к процитированному сообщению: вне окна виртуализации его узла
   *  в DOM нет, поэтому прыгаем по индексу строки, а не по ref. */
  function scrollToMessage(tgId: number) {
    const idx = indexByTgId.get(tgId);
    if (idx == null) return;
    virtualizer.scrollToIndex(idx, { align: "center", behavior: "smooth" });
    setHighlightId(tgId);
    window.setTimeout(() => setHighlightId((cur) => (cur === tgId ? null : cur)), 1600);
  }

  if (!selectedAccountId || !selectedChatId || !chat) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
        <MessagesSquare className="h-12 w-12 opacity-20" />
        <p className="text-sm">Выбери чат, чтобы открыть переписку</p>
      </div>
    );
  }

  const title = chat.title || (chat.username ? `@${chat.username}` : `peer ${chat.tg_peer_id}`);
  const accName = account
    ? account.username
      ? `@${account.username}`
      : [account.first_name, account.last_name].filter(Boolean).join(" ") || account.phone
    : null;
  const accColor = account ? colorFromString(account.id) : undefined;

  return (
    <div className="relative flex min-w-0 flex-1 flex-col">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card/50 px-5 py-3">
        <div className="flex min-w-[220px] flex-1 items-center gap-3">
          <Avatar
            name={title}
            initials={initials(title.split(" ")[0], title.split(" ")[1], "?")}
            size={36}
            src={chat.avatar_media_id ? api.mediaUrl(chat.avatar_media_id) : null}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-semibold">{title}</div>
              {showAccountBadge && account && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-muted-foreground"
                  title={account.phone}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: accColor }}
                  />
                  <span className="text-foreground">{accName}</span>
                  <span className="font-mono opacity-70">· {account.phone}</span>
                </span>
              )}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">
              {/* Тег дублирует title, когда title пуст — тогда не повторяем. */}
              {chat.username && chat.title && (
                <span className="text-foreground">@{chat.username} · </span>
              )}
              {chat.kind} · id {chat.tg_peer_id}
            </div>
          </div>
        </div>
        {/* Кнопки не сжимаются и не режутся: если рядом с именем чата им уже
            тесно, шапка переносит их целиком на вторую строку. */}
        <div className="flex shrink-0 justify-end gap-2">
          <Button
            variant={showDeleted ? "default" : "outline"}
            size="sm"
            onClick={() => setShowDeleted((v) => !v)}
            title={showDeleted ? "Скрыть удалённые сообщения" : "Показать сообщения, удалённые собеседником"}
          >
            {showDeleted ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showDeleted ? "Скрыть удалённые" : "Показать удалённые"}
          </Button>
          {chat.kind === "private" ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (confirm(`Заблокировать ${chatName}? Этот пользователь больше не сможет вам писать.`)) blockUser.mutate();
              }}
              disabled={blockUser.isPending}
            >
              <Ban className="h-4 w-4" /> Заблокировать
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => {
                if (confirm(`Выйти из «${title}»?`)) leaveChat.mutate();
              }}
              disabled={leaveChat.isPending}
            >
              <LogOut className="h-4 w-4" /> Выйти
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (confirm(`Удалить диалог «${title}»?`)) deleteDialog.mutate();
            }}
            disabled={deleteDialog.isPending}
          >
            <Trash2 className="h-4 w-4" /> Удалить
          </Button>
        </div>
      </header>

      {offline && (
        <div className="flex items-start gap-2 border-b border-destructive/30 bg-destructive/10 px-5 py-2 text-xs text-destructive">
          <AlertCircle className="mt-px h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Аккаунт не на связи — показана история из базы.</div>
            <div className="opacity-90">{offline}</div>
          </div>
        </div>
      )}

      <div ref={scrollRef} onScroll={onScroll} className="relative flex-1 overflow-y-auto bg-background">
        {/* Индикатор догрузки — оверлеем: строкой в потоке он сдвигал бы
            измеренные позиции виртуализатора и дёргал скролл. */}
        {(isFetchingNextPage || historyState === "loading") && (
          <div className="pointer-events-none sticky top-0 z-10 py-2 text-center text-xs text-muted-foreground">
            Загрузка истории…
          </div>
        )}
        {historyState === "slow" && rows.length > 0 && (
          <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 text-center text-xs text-muted-foreground">
            Telegram отвечает долго — история ещё грузится, показано не всё.
            <button type="button" onClick={loadHistory} className="underline underline-offset-2">
              Запросить ещё раз
            </button>
          </div>
        )}
        {historyState === "failed" && rows.length > 0 && (
          <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 text-center text-xs text-destructive">
            Не удалось подтянуть историю из Telegram.
            <button type="button" onClick={loadHistory} className="underline underline-offset-2">
              Повторить
            </button>
          </div>
        )}
        {isFetching && messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Загрузка…</div>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-muted-foreground">
            <MessagesSquare className="h-10 w-10 opacity-20" />
            {offline ? (
              <p className="max-w-sm text-sm">
                В базе нет сообщений этого чата. Аккаунт не на связи — подтянуть историю
                из Telegram сейчас нельзя, так что пусто здесь ≠ пусто в Telegram.
              </p>
            ) : historyState === "slow" ? (
              <div className="flex flex-col items-center gap-2">
                <p className="max-w-sm text-sm">
                  Telegram отвечает долго — история ещё грузится. Пусто здесь пока ≠ пусто в
                  Telegram.
                </p>
                <Button variant="outline" size="sm" onClick={loadHistory}>
                  <RotateCw className="h-4 w-4" /> Запросить ещё раз
                </Button>
              </div>
            ) : historyState === "failed" ? (
              <div className="flex flex-col items-center gap-2">
                <p className="max-w-sm text-sm">
                  Не удалось подтянуть историю из Telegram — в базе её пока нет.
                </p>
                <Button variant="outline" size="sm" onClick={loadHistory}>
                  <RotateCw className="h-4 w-4" /> Повторить
                </Button>
              </div>
            ) : historyState === "loading" || !chat.history_synced_at ? (
              <p className="text-sm">Подтягиваем историю из Telegram…</p>
            ) : (
              <p className="max-w-sm text-sm">
                В Telegram в этом чате нет сообщений — история загружена полностью.
              </p>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl px-5">
            <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((v) => {
                const row = rows[v.index];
                return (
                  <div
                    key={v.key}
                    data-index={v.index}
                    ref={virtualizer.measureElement}
                    className="absolute left-0 top-0 w-full"
                    style={{ transform: `translateY(${v.start}px)` }}
                  >
                    {row.kind === "day" ? (
                      <div className="flex items-center justify-center py-3">
                        <span className="rounded-full bg-card px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {row.label}
                        </span>
                      </div>
                    ) : row.kind === "service" ? (
                      <ServiceRow m={row.message} />
                    ) : row.kind === "pending" ? (
                      <div className="pb-1">
                        <PendingBubble
                          item={row.item}
                          onRetry={() => retryPending(row.item)}
                          onDrop={() => useOutbox.getState().remove(row.item.localId)}
                        />
                      </div>
                    ) : row.kind === "album" ? (
                      <div className="pb-1">
                        <AlbumBubble
                          messages={row.messages}
                          grouped={row.grouped}
                          read={
                            chat.outbox_read_max_id != null &&
                            row.messages[row.messages.length - 1].tg_message_id <= chat.outbox_read_max_id
                          }
                          highlighted={row.messages.some((m) => highlightId === m.tg_message_id)}
                          onReply={() => setReplyTo(row.messages[0])}
                        />
                      </div>
                    ) : (
                      <div className="pb-1">
                        <Bubble
                          m={row.message}
                          deleted={row.message.deleted_at != null}
                          read={
                            chat.outbox_read_max_id != null &&
                            row.message.tg_message_id <= chat.outbox_read_max_id
                          }
                          grouped={row.grouped}
                          quoted={
                            row.message.reply_to_tg_id != null
                              ? byTgId.get(row.message.reply_to_tg_id) ?? null
                              : null
                          }
                          quoteLabel={
                            row.message.reply_to_tg_id != null
                              ? (() => {
                                  const q = byTgId.get(row.message.reply_to_tg_id!);
                                  return q ? senderLabel(q) : "Сообщение";
                                })()
                              : null
                          }
                          highlighted={highlightId === row.message.tg_message_id}
                          onReply={() => setReplyTo(row.message)}
                          onQuoteClick={
                            row.message.reply_to_tg_id != null &&
                            indexByTgId.has(row.message.reply_to_tg_id)
                              ? () => scrollToMessage(row.message.reply_to_tg_id!)
                              : undefined
                          }
                          onButton={(r, c, btn) => handleButton(row.message, r, c, btn)}
                          buttonsDisabled={buttonsBusy}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showJump && (
        <button
          type="button"
          onClick={jumpToBottom}
          className="absolute bottom-28 right-8 z-10 flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs shadow-lg transition-colors hover:bg-accent"
          title="К последним сообщениям"
        >
          <ArrowDown className="h-4 w-4" />
          {missedCount > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {missedCount}
            </span>
          )}
        </button>
      )}

      {offline ? (
        <div className="flex items-center gap-2 border-t border-border bg-card/40 px-5 py-4 text-xs text-muted-foreground">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
          Отправка недоступна: аккаунт не подключён к Telegram.
        </div>
      ) : (
        <Composer
          accountId={selectedAccountId}
          chatId={selectedChatId}
          replyTo={replyTo}
          replyLabel={replyTo ? senderLabel(replyTo) : undefined}
          onCancelReply={() => setReplyTo(null)}
          blocked={chat.peer_blocked_at ? chat.peer_blocked_reason || "blocked" : null}
        />
      )}
    </div>
  );
}

function Bubble({
  m,
  deleted,
  read,
  grouped,
  quoted,
  quoteLabel,
  highlighted,
  onReply,
  onQuoteClick,
  onButton,
  buttonsDisabled,
}: {
  m: Message;
  deleted?: boolean;
  /** Наше сообщение прочитано собеседником (read receipt Telegram). */
  read?: boolean;
  grouped: boolean;
  quoted?: Message | null;
  quoteLabel?: string | null;
  highlighted?: boolean;
  onReply?: () => void;
  onQuoteClick?: () => void;
  onButton?: (row: number, col: number, btn: MessageButton) => void;
  buttonsDisabled?: boolean;
}) {
  const out = m.direction === "out";
  const replyBtn = (
    <button
      type="button"
      onClick={onReply}
      title="Ответить"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100"
    >
      <Reply className="h-4 w-4" />
    </button>
  );
  return (
    <div className={cn("group flex items-center gap-1.5", out ? "justify-end" : "justify-start")}>
      {out && replyBtn}
      <div
        className={cn(
          "max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm transition-shadow",
          out
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-card text-card-foreground border border-border rounded-bl-md",
          grouped && (out ? "rounded-tr-md" : "rounded-tl-md"),
          deleted && "opacity-70 border-2 border-dashed border-destructive/50 line-through decoration-destructive/40",
          highlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background"
        )}
      >
        {m.reply_to_tg_id != null && (
          <QuotedReply quoted={quoted} label={quoteLabel} out={out} onClick={onQuoteClick} />
        )}
        {m.media_id ? (
          <div className="space-y-1.5">
            <MediaPreview mediaId={m.media_id} media={m.media} out={out} />
            {m.text ? <div className="whitespace-pre-wrap break-words">{m.text}</div> : null}
          </div>
        ) : m.text ? (
          <div className="whitespace-pre-wrap break-words">{m.text}</div>
        ) : m.note ? (
          <div className="whitespace-pre-wrap break-words">{m.note}</div>
        ) : (
          <span className="italic opacity-70">сообщение без текста</span>
        )}
        <div className={cn("mt-0.5 flex items-center gap-1 text-[10px] opacity-70 no-underline", out ? "justify-end" : "justify-start")}>
          {deleted && (
            <>
              <span className="font-medium uppercase tracking-wide text-destructive">удалено</span>
              <span>·</span>
            </>
          )}
          <span className="font-mono">#{m.tg_message_id}</span>
          <span>·</span>
          <span>{fmtTime(m.sent_at)}</span>
          {out && (
            <span title={read ? "Прочитано собеседником" : "Доставлено, не прочитано"}>
              {read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
            </span>
          )}
        </div>
        {m.buttons && m.buttons.rows.length > 0 && (
          <MessageKeyboard buttons={m.buttons} onButton={onButton} disabled={buttonsDisabled} />
        )}
      </div>
      {!out && replyBtn}
    </div>
  );
}

/** Служебное сообщение (звонок, «теперь в Telegram», закреп, ...) — строка по
 *  центру ленты, как в официальном клиенте. */
function ServiceRow({ m }: { m: Message }) {
  return (
    <div className="flex justify-center py-1.5">
      <span
        className={cn(
          "max-w-[85%] rounded-full bg-card px-3 py-1 text-center text-xs text-muted-foreground",
          m.deleted_at && "line-through opacity-70"
        )}
        title={`#${m.tg_message_id}`}
      >
        {m.text || "Служебное сообщение"} · {fmtTime(m.sent_at)}
      </span>
    </div>
  );
}

/** Вложение, которое ещё лежит в Telegram: крупные файлы воркер сразу не
 *  качает (как и официальный клиент) — оператор скачивает по клику и видит
 *  прогресс, а не пустой пузырь. */
function RemoteMediaCard({
  media,
  out,
  compact,
}: {
  media: NonNullable<Message["media"]>;
  out?: boolean;
  /** Тайл альбома: квадрат, всё по центру. */
  compact?: boolean;
}) {
  const dl = useMediaDownloads((s) => s.items[media.id]);
  const busy = dl != null && dl.status !== "failed";
  const pct = dl && dl.total > 0 ? Math.min(100, (dl.received / dl.total) * 100) : 0;

  function start() {
    useMediaDownloads.getState().start(media.id);
    api.fetchMedia(media.id).catch((e) =>
      useMediaDownloads.getState().fail(media.id, (e as ApiError).message ?? String(e))
    );
  }

  const status =
    dl?.status === "downloading"
      ? `${bytesHuman(dl.received)} из ${bytesHuman(dl.total || media.size)}`
      : dl?.status === "queued"
        ? "запрашиваем у Telegram…"
        : dl?.status === "failed"
          ? dl.error
          : `${bytesHuman(media.size)} · не скачано`;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg p-2",
        out ? "bg-black/10" : "bg-secondary",
        compact ? "h-full w-full flex-col justify-center text-center" : "min-w-[220px] max-w-full"
      )}
    >
      <button
        type="button"
        onClick={start}
        disabled={busy}
        title={dl?.status === "failed" ? "Попробовать ещё раз" : "Скачать из Telegram"}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-default"
      >
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : dl?.status === "failed" ? (
          <RotateCw className="h-5 w-5" />
        ) : (
          <Download className="h-5 w-5" />
        )}
      </button>
      <div className={cn("min-w-0", !compact && "flex-1")}>
        <div className="truncate text-xs font-medium">
          {media.original_name || mediaKindLabel(media.kind)}
        </div>
        <div className={cn("text-[11px] opacity-70", dl?.status === "failed" && "text-destructive opacity-100")}>
          {status}
        </div>
        {dl?.status === "downloading" && (
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-black/15">
            <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Пузырь сообщения, которое ещё не подтверждено Telegram. */
function PendingBubble({
  item,
  onRetry,
  onDrop,
}: {
  item: PendingMessage;
  onRetry: () => void;
  onDrop: () => void;
}) {
  const failed = item.status === "failed";
  return (
    <div className="flex items-center justify-end gap-1.5">
      <div
        className={cn(
          "max-w-[78%] rounded-2xl rounded-br-md px-3.5 py-2 text-sm shadow-sm",
          failed
            ? "border border-destructive/50 bg-destructive/10 text-foreground"
            : "bg-primary/70 text-primary-foreground"
        )}
      >
        {item.fileName && (
          <div className="mb-1 truncate text-xs opacity-80">📎 {item.fileName}</div>
        )}
        {item.text ? (
          <div className="whitespace-pre-wrap break-words">{item.text}</div>
        ) : (
          <span className="italic opacity-70">вложение</span>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] opacity-80">
          {failed ? (
            <>
              <AlertCircle className="h-3 w-3 text-destructive" />
              <span className="text-destructive">{item.error || "не отправлено"}</span>
              <button
                type="button"
                onClick={onRetry}
                className="ml-1 flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent"
                title="Повторить отправку"
              >
                <RotateCw className="h-3 w-3" /> Ещё раз
              </button>
              <button
                type="button"
                onClick={onDrop}
                className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent"
                title="Убрать"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          ) : (
            <>
              <Clock className="h-3 w-3" />
              <span>отправляется…</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Несколько фото/видео из одного Telegram-альбома (общий media_group_id) —
 *  один пузырь с сеткой вложений вместо N отдельных сообщений. */
function AlbumBubble({
  messages,
  grouped,
  read,
  highlighted,
  onReply,
}: {
  messages: Message[];
  grouped: boolean;
  read?: boolean;
  highlighted?: boolean;
  onReply?: () => void;
}) {
  const out = messages[0].direction === "out";
  const last = messages[messages.length - 1];
  // Telegram кладёт подпись альбома на одно из сообщений группы — берём первую непустую.
  const caption = messages.find((m) => m.text)?.text ?? null;
  const deleted = messages.every((m) => m.deleted_at != null);
  const replyBtn = (
    <button
      type="button"
      onClick={onReply}
      title="Ответить"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100"
    >
      <Reply className="h-4 w-4" />
    </button>
  );
  return (
    <div className={cn("group flex items-center gap-1.5", out ? "justify-end" : "justify-start")}>
      {out && replyBtn}
      <div
        className={cn(
          "max-w-[78%] rounded-2xl p-1.5 text-sm shadow-sm transition-shadow",
          out
            ? "bg-primary text-primary-foreground rounded-br-md"
            : "bg-card text-card-foreground border border-border rounded-bl-md",
          grouped && (out ? "rounded-tr-md" : "rounded-tl-md"),
          deleted && "opacity-70 border-2 border-dashed border-destructive/50",
          highlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background"
        )}
      >
        <MediaGrid messages={messages} />
        {caption && <div className="whitespace-pre-wrap break-words px-1.5 pt-1.5">{caption}</div>}
        <div
          className={cn(
            "mt-0.5 flex items-center gap-1 px-1.5 pb-0.5 text-[10px] opacity-70",
            out ? "justify-end" : "justify-start"
          )}
        >
          {deleted && (
            <>
              <span className="font-medium uppercase tracking-wide text-destructive">удалено</span>
              <span>·</span>
            </>
          )}
          <span className="font-mono">#{last.tg_message_id}</span>
          <span>·</span>
          <span>{fmtTime(last.sent_at)}</span>
          {out && (
            <span title={read ? "Прочитано собеседником" : "Доставлено, не прочитано"}>
              {read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />}
            </span>
          )}
        </div>
      </div>
      {!out && replyBtn}
    </div>
  );
}

type LightboxItem = { media_id: string; media: Message["media"] };

/** Сетка вложений альбома: 1 колонка на одиночное фото/видео, 2×N иначе; если
 *  вложений больше 4 — на последнем тайле оверлей «+N», клик открывает галерею
 *  с пролистыванием по всем элементам группы. */
function MediaGrid({ messages }: { messages: Message[] }) {
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  // В галерею идут только скачанные: остальные показываем карточкой «Скачать».
  const items: LightboxItem[] = messages
    .filter((m) => m.media_id && m.media?.downloaded !== false)
    .map((m) => ({ media_id: m.media_id!, media: m.media }));
  const remote = messages.filter((m) => m.media && !m.media.downloaded).map((m) => m.media!);
  const shown = items.slice(0, Math.max(0, 4 - remote.length));
  const overflow = items.length - shown.length;

  return (
    <>
      <div
        className={cn(
          "grid gap-0.5 overflow-hidden rounded-xl",
          shown.length + remote.length === 1 ? "grid-cols-1" : "grid-cols-2"
        )}
      >
        {remote.map((media) => (
          <div key={media.id} className="aspect-square w-full overflow-hidden">
            <RemoteMediaCard media={media} compact />
          </div>
        ))}
        {shown.map((item, i) => (
          <button
            key={item.media_id}
            type="button"
            onClick={() => setGalleryIndex(i)}
            className="relative aspect-square w-full overflow-hidden bg-black/5"
          >
            <MediaTile media={item.media} media_id={item.media_id} />
            {i === shown.length - 1 && overflow > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white">
                +{overflow}
              </div>
            )}
          </button>
        ))}
      </div>
      {galleryIndex != null && (
        <MediaLightbox
          items={items}
          index={galleryIndex}
          onIndexChange={setGalleryIndex}
          onClose={() => setGalleryIndex(null)}
        />
      )}
    </>
  );
}

/** Один тайл в сетке альбома: превью с плейсхолдером на время загрузки. */
function MediaTile({ media, media_id: mediaId }: LightboxItem) {
  const url = api.mediaUrl(mediaId);
  const [loaded, setLoaded] = useState(false);
  const isVideo = media?.kind === "video" || (media?.mime ?? "").startsWith("video/");
  return (
    <div className="relative h-full w-full">
      {!loaded && <div className="absolute inset-0 animate-pulse bg-muted" />}
      {isVideo ? (
        <video
          src={url}
          className="h-full w-full object-cover"
          preload="metadata"
          muted
          onLoadedData={() => setLoaded(true)}
        />
      ) : (
        <img
          src={url}
          alt={media?.original_name ?? "photo"}
          className="h-full w-full object-cover"
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
      )}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/50">
            <Play className="h-4 w-4 fill-white text-white" />
          </div>
        </div>
      )}
    </div>
  );
}

/** Компактный кастомный плеер голосовых/аудио: без waveform, но play/pause,
 *  полоса прогресса с click-to-seek и текущее/общее время. */
function VoicePlayer({ url, out }: { url: string; out?: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  }

  function seek(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  }

  const pct = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div
      className={cn(
        "flex w-full max-w-[220px] items-center gap-2 rounded-full px-1.5 py-1.5",
        out ? "bg-black/10" : "bg-secondary"
      )}
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
      />
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          out ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
        )}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-[1px]" />}
      </button>
      <div className="flex-1 cursor-pointer py-2" onClick={seek}>
        <div className="relative h-1 rounded-full bg-black/15">
          <div
            className={cn("absolute inset-y-0 left-0 rounded-full", out ? "bg-primary-foreground" : "bg-primary")}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="shrink-0 font-mono text-[11px] opacity-70">
        {fmtDuration(playing || current > 0 ? current : duration)}
      </span>
    </div>
  );
}

/** Кружочек (video_note): круглая маска, свой play/pause вместо нативных
 *  controls (их не получится аккуратно обрезать по кругу) и таймер длительности. */
function VideoNotePreview({ url }: { url: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  function toggle() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  }

  return (
    <div
      className="relative h-48 w-48 cursor-pointer overflow-hidden rounded-full bg-black"
      onClick={toggle}
    >
      <video
        ref={videoRef}
        src={url}
        className="h-full w-full object-cover"
        preload="metadata"
        loop
        playsInline
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
            <Play className="h-6 w-6 fill-white text-white" />
          </div>
        </div>
      )}
      {duration > 0 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">
          {fmtDuration(duration)}
        </div>
      )}
    </div>
  );
}

function MessageKeyboard({
  buttons,
  onButton,
  disabled,
}: {
  buttons: MessageButtons;
  onButton?: (row: number, col: number, btn: MessageButton) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {buttons.rows.map((row, ri) => (
        <div key={ri} className="flex gap-1">
          {row.map((btn, ci) => {
            const isLink = btn.kind === "url" || btn.kind === "webview";
            return (
              <button
                key={ci}
                type="button"
                disabled={disabled}
                onClick={() => onButton?.(ri, ci, btn)}
                title={btn.url ?? btn.kind}
                className="flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md border border-border bg-background/80 px-2 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLink && <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />}
                <span className="truncate">{btn.text || "—"}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function QuotedReply({
  quoted,
  label,
  out,
  onClick,
}: {
  quoted?: Message | null;
  label?: string | null;
  out: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "mb-1 flex w-full flex-col items-start gap-0.5 rounded-md border-l-2 px-2 py-1 text-left",
        out ? "border-primary-foreground/60 bg-primary-foreground/10" : "border-primary/60 bg-primary/5",
        onClick && "cursor-pointer hover:opacity-90"
      )}
    >
      <span className="text-[11px] font-medium opacity-90">{label ?? "Сообщение"}</span>
      <span className="line-clamp-1 max-w-[16rem] text-xs opacity-70">
        {quoted ? messagePreview(quoted) : "исходное сообщение недоступно"}
      </span>
    </button>
  );
}

function MediaPreview({ mediaId, media, out }: { mediaId: string; media: Message["media"]; out?: boolean }) {
  const url = api.mediaUrl(mediaId);
  const kind = media?.kind;
  const mime = media?.mime ?? "";
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (media && !media.downloaded) {
    return <RemoteMediaCard media={media} out={out} />;
  }

  if (kind === "photo" || mime.startsWith("image/")) {
    return (
      <>
        <div className="relative inline-block max-h-80 max-w-full">
          {!loaded && <div className="absolute inset-0 animate-pulse rounded-lg bg-muted" />}
          <img
            src={url}
            alt={media?.original_name ?? "photo"}
            className="max-h-80 max-w-full cursor-zoom-in rounded-lg object-contain"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onClick={() => setOpen(true)}
          />
        </div>
        {open && (
          <MediaLightbox
            items={[{ media_id: mediaId, media }]}
            index={0}
            onIndexChange={() => {}}
            onClose={() => setOpen(false)}
          />
        )}
      </>
    );
  }

  if (kind === "video_note") {
    return <VideoNotePreview url={url} />;
  }

  if (kind === "video" || mime.startsWith("video/")) {
    return (
      <div className="relative inline-block max-h-80 max-w-full">
        {!loaded && <div className="absolute inset-0 animate-pulse rounded-lg bg-muted" />}
        <video
          src={url}
          controls
          className="max-h-80 max-w-full rounded-lg"
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
        />
      </div>
    );
  }

  if (kind === "voice" || kind === "audio" || mime.startsWith("audio/")) {
    return <VoicePlayer url={url} out={out} />;
  }

  const name = media?.original_name ?? `${mediaId.slice(0, 8)}…`;
  const sizeKb = media?.size ? ` · ${(media.size / 1024).toFixed(0)} КБ` : "";
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline-offset-2 hover:underline">
      <span>📎</span>
      <span className="text-xs">{name}{sizeKb}</span>
    </a>
  );
}

/** Полноэкранный просмотр фото/видео, как в Telegram, вместо перехода на
 *  отдельную вкладку. Несколько элементов (альбом) — со стрелками/клавишами
 *  пролистывания. Esc/клик по фону закрывает — Radix Dialog делает это из коробки. */
function MediaLightbox({
  items,
  index,
  onIndexChange,
  onClose,
}: {
  items: LightboxItem[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
}) {
  const item = items[index];
  const url = api.mediaUrl(item.media_id);
  const isVideo = item.media?.kind === "video" || (item.media?.mime ?? "").startsWith("video/");
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1);
      if (e.key === "ArrowRight" && hasNext) onIndexChange(index + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onIndexChange]);

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/85 data-[state=open]:animate-fade-in" />
        {/* Content сжат по картинке (не на весь экран) — клик по фону, вне
            Content, попадает на Overlay, и Radix закрывает диалог сам. */}
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 focus:outline-none"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">{item.media?.original_name ?? "media"}</DialogPrimitive.Title>
          {isVideo ? (
            <video
              key={item.media_id}
              src={url}
              controls
              autoPlay
              className="max-h-[80vh] max-w-full rounded-md object-contain shadow-2xl"
            />
          ) : (
            <img
              src={url}
              alt={item.media?.original_name ?? "photo"}
              className="max-h-[80vh] max-w-full rounded-md object-contain shadow-2xl"
            />
          )}
          <div className="flex items-center gap-3">
            {items.length > 1 && (
              <span className="text-xs text-white/70">{index + 1} / {items.length}</span>
            )}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              download
              className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs text-white transition-colors hover:bg-white/20"
            >
              <Download className="h-3.5 w-3.5" /> Скачать
            </a>
          </div>
          {hasPrev && (
            <button
              type="button"
              onClick={() => onIndexChange(index - 1)}
              className="fixed left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              title="Предыдущее"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {hasNext && (
            <button
              type="button"
              onClick={() => onIndexChange(index + 1)}
              className="fixed right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              title="Следующее"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
          <DialogPrimitive.Close className="fixed right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Строка виртуализированной ленты: разделитель дня, сообщение, альбом (несколько
 *  фото/видео с общим media_group_id) или пузырь очереди. */
type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "service"; key: string; message: Message }
  | { kind: "message"; key: string; message: Message; grouped: boolean }
  | { kind: "album"; key: string; messages: Message[]; grouped: boolean }
  | { kind: "pending"; key: string; item: PendingMessage };

/** Разворачивает историю и очередь отправки в плоский список строк.
 *  Ключи устойчивы к догрузке истории — на них держится позиция скролла.
 *  Подряд идущие сообщения одного направления с одинаковым непустым
 *  media_group_id — это один Telegram-альбом, схлопываем их в одну строку. */
function buildRows(messages: Message[], pending: PendingMessage[]): Row[] {
  const asc = [...messages].sort((a, b) => a.tg_message_id - b.tg_message_id);
  const rows: Row[] = [];
  let currentDay: string | null = null;
  let prev: Message | null = null;

  let i = 0;
  while (i < asc.length) {
    const m = asc[i];
    const d = new Date(m.sent_at);
    const key = d.toDateString();
    if (key !== currentDay) {
      currentDay = key;
      prev = null;
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      const label =
        key === today ? "Сегодня" : key === yesterday ? "Вчера" : d.toLocaleDateString();
      rows.push({ kind: "day", key: `day:${key}`, label });
    }

    // Служебное (звонок, «теперь в Telegram», ...) — строка по центру, как в
    // официальном клиенте, и не склеивается с соседними пузырями.
    if (m.service) {
      rows.push({ kind: "service", key: `m:${m.tg_message_id}`, message: m });
      prev = null;
      i += 1;
      continue;
    }

    const cluster = [m];
    if (m.media_group_id != null) {
      let j = i + 1;
      while (
        j < asc.length &&
        asc[j].media_group_id === m.media_group_id &&
        asc[j].direction === m.direction &&
        new Date(asc[j].sent_at).toDateString() === key
      ) {
        cluster.push(asc[j]);
        j++;
      }
    }

    const grouped = prev != null && prev.direction === m.direction && sameMinute(prev.sent_at, m.sent_at);
    if (cluster.length > 1) {
      rows.push({ kind: "album", key: `alb:${m.media_group_id}`, messages: cluster, grouped });
    } else {
      rows.push({ kind: "message", key: `m:${m.tg_message_id}`, message: m, grouped });
    }
    prev = cluster[cluster.length - 1];
    i += cluster.length;
  }

  for (const item of pending) {
    rows.push({ kind: "pending", key: `p:${item.localId}`, item });
  }
  return rows;
}

function sameMinute(a: string, b: string) {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 60_000;
}
